import { useEffect, useRef, useState } from 'react';
import { db } from '@/lib/db';
import type { Member, PostEnriched } from '@/lib/db/types';
import type { UnlockedAchievement } from '@/lib/achievements/process';
import { Header, type ShellTab } from '@/components/shell/Header';
import { BottomNav, type AppTab, type NavTab } from '@/components/shell/BottomNav';
import { Sheet } from '@/components/shared/Sheet';
import { ActivitySheet } from '@/components/shell/ActivitySheet';
import { FAQSheet } from '@/components/shell/FAQSheet';
import { FeedTab } from '@/components/FeedTab';
import { BoardTab } from '@/components/BoardTab';
import { ProfileTab } from '@/components/ProfileTab';
import { EventsTab } from '@/components/EventsTab';
import { FoodTab } from '@/components/FoodTab';
import { CreateRoundTab, type CreateRoundTabHandle } from '@/components/rounds/CreateRoundTab';
import { HoleInOneCelebration } from '@/components/play/HoleInOneCelebration';
import { CallYourShotSheet } from '@/components/shots/CallYourShotSheet';
import { InviteMemberModal } from '@/components/profile/InviteMemberModal';
import { ComposeRound } from '@/components/feed/ComposeRound';
import { MembersSheet } from '@/components/profile/MembersSheet';
import { ProfileModal } from '@/components/profile/ProfileModal';
import { GhinConnectSheet } from '@/components/profile/GhinConnectSheet';
import { GameDetailSheet } from '@/components/games/GameDetailSheet';
import { WagerModal } from '@/components/feed/WagerModal';
import { GameResultModal } from '@/components/games/GameResultModal';
import { RoundResultsSheet } from '@/components/rounds/RoundResultsSheet';
import { AchievementCelebrationToast } from '@/components/feed/AchievementCelebrationToast';
import { SummaryModal } from '@/components/feed/SummaryModal';
import { GamesLibrarySheet } from '@/components/games/GamesLibrarySheet';
import { ScoreTicker } from '@/components/ScoreTicker';
import { loadPlayTabData } from '@/lib/play-tab-data';

interface Props {
  member: Member;
}

interface AceCelebration {
  holeNum: number;
  courseName: string;
  courseId?: string;
}

interface GameResultState {
  post: PostEnriched;
  gameIndex: number;
}

export function MainApp({ member: initialMember }: Props) {
  const [member, setMember] = useState(initialMember);
  const [tab, setTab] = useState<AppTab>('home');
  const [unreadCount, setUnreadCount] = useState(0);
  const [openActivity, setOpenActivity] = useState(false);
  const [openFAQ, setOpenFAQ] = useState(false);
  const [openCallShot, setOpenCallShot] = useState(false);
  const [openInvite, setOpenInvite] = useState(false);
  const [openMembers, setOpenMembers] = useState(false);
  const [openHandicap, setOpenHandicap] = useState(false);
  const [composeMode, setComposeMode] = useState<'quick' | 'photo' | 'full' | null>(null);
  const [profileId, setProfileId] = useState<string | null>(null);
  const [gameId, setGameId] = useState<string | null>(null);
  const [wagerState, setWagerState] = useState<{ post: PostEnriched; isNew?: boolean } | null>(null);
  const [gameResult, setGameResult] = useState<GameResultState | null>(null);
  const [settlementRoundId, setSettlementRoundId] = useState<string | null>(null);
  const [settlementFocusMoney, setSettlementFocusMoney] = useState(false);
  const [memberMap, setMemberMap] = useState<Record<string, import('@/lib/db/types').MemberPublic>>({});
  const [courseNames, setCourseNames] = useState<Record<string, string>>({});
  const [aceCelebration, setAceCelebration] = useState<AceCelebration | null>(null);
  const [achievementToast, setAchievementToast] = useState<UnlockedAchievement | null>(null);
  const [callSettleToast, setCallSettleToast] = useState<string | null>(null);
  const [summaryPost, setSummaryPost] = useState<PostEnriched | null>(null);
  const [openGamesLib, setOpenGamesLib] = useState(false);
  const [openCreateEvent, setOpenCreateEvent] = useState(false);
  const [roundsOpen, setRoundsOpen] = useState(false);
  const roundTabRef = useRef<CreateRoundTabHandle>(null);

  useEffect(() => {
    setMember(initialMember);
  }, [initialMember]);

  useEffect(() => {
    void loadPlayTabData();
    db.notifications.unreadCount().then(setUnreadCount);
    const unsub = db.notifications.subscribe(() => {
      db.notifications.unreadCount().then(setUnreadCount);
    });
    void Promise.all([
      db.courses.list().then((courses) => {
        setCourseNames(Object.fromEntries(courses.map((c) => [c.id, c.name])));
      }),
      db.members.list({ limit: 100 }).then((people) => {
        setMemberMap(Object.fromEntries(people.map((m) => [m.id, m])));
      }),
    ]);
    return unsub;
  }, []);

  useEffect(() => {
    const onCallSettled = (e: Event) => {
      const msg = (e as CustomEvent<string>).detail;
      if (msg) {
        setCallSettleToast(msg);
        window.setTimeout(() => setCallSettleToast(null), 5000);
      }
    };
    window.addEventListener('bagtag:call-settled', onCallSettled);
    return () => window.removeEventListener('bagtag:call-settled', onCallSettled);
  }, []);

  const refreshFeed = () => {
    window.dispatchEvent(new Event('bagtag:refresh-feed'));
  };

  const refreshGames = () => {
    window.dispatchEvent(new Event('bagtag:refresh-games'));
  };

  const handleAchievement = (achievements: UnlockedAchievement[]) => {
    if (achievements[0]) setAchievementToast(achievements[0]);
    refreshFeed();
  };

  const handleAce = (holeNum: number, courseName: string) => {
    setAceCelebration({ holeNum, courseName });
  };

  const handleRoundPublished = async (postId: string) => {
    refreshFeed();
    try {
      const post = await db.posts.get(postId);
      if (post?.type === 'round') setSummaryPost(post);
    } catch {
      // Recap is optional — feed refresh already happened
    }
  };

  const recordAce = async (share: boolean) => {
    if (!aceCelebration) return;
    try {
      const ace = await db.aces.create({ hole_num: aceCelebration.holeNum, notes: aceCelebration.courseName });
      if (share) {
        await db.posts.create({
          type: 'ace',
          ace_id: ace.id,
          body: `Hole-in-one on hole ${aceCelebration.holeNum} at ${aceCelebration.courseName}!`,
        });
      }
      refreshFeed();
    } finally {
      setAceCelebration(null);
    }
  };

  const shellTab = tab;

  const goToTab = (t: NavTab) => {
    setRoundsOpen(false);
    setTab(t);
  };

  const openClubhouse = () => {
    setRoundsOpen(false);
    setTab('food');
  };

  return (
    <div className="mx-auto flex h-[100dvh] max-w-phone flex-col overflow-hidden bg-cream">
      <div
        className="shrink-0"
        style={{ paddingTop: 'env(safe-area-inset-top, 0px)' }}
      >
        <ScoreTicker onScoreTap={(s) => setProfileId(s.member_id)} />
        <Header
          tab={shellTab as ShellTab}
          unread={unreadCount}
          onOpenActivity={() => setOpenActivity(true)}
          onOpenFAQ={() => setOpenFAQ(true)}
        />
      </div>

      <main
        className="flex-1 overflow-y-auto px-4"
        style={{ paddingBottom: 'calc(var(--bottom-nav-height) + env(safe-area-inset-bottom, 0px))' }}
      >
        <div className={tab === 'home' ? undefined : 'hidden'}>
          <FeedTab
            member={member}
            onComposeRound={() => setComposeMode('quick')}
            onPostStatus={() => setComposeMode('photo')}
            onInviteMember={() => setOpenInvite(true)}
            onCallYourShot={() => setOpenCallShot(true)}
            onSearchMembers={() => setOpenMembers(true)}
            onUpdateHandicap={() => setOpenHandicap(true)}
            onOpenClubhouse={openClubhouse}
            onOpenProfile={setProfileId}
            onOpenGame={setGameId}
            onOpenWager={(post, opts) => setWagerState({ post, isNew: opts?.isNew })}
            onOpenGameResult={(post, gameIndex) => setGameResult({ post, gameIndex })}
            onOpenMoneyResults={async (post) => {
              const openGroupResults = (id: string) => {
                setSettlementFocusMoney(true);
                setSettlementRoundId(id);
              };
              const fromMeta = post.metadata?.groupRoundId;
              if (fromMeta) {
                openGroupResults(fromMeta);
                return;
              }
              try {
                const id = await db.groupRounds.findIdByLegacyPostId(post.id);
                if (id) {
                  openGroupResults(id);
                  return;
                }
              } catch {
                // Fall through to feed snapshot settlement
              }
              const games = post.metadata?.games || [];
              const withSettle = games.findIndex((g) => g.settlement && Object.keys(g.settlement).length > 0);
              if (withSettle >= 0) {
                setGameResult({ post, gameIndex: withSettle });
              }
            }}
          />
        </div>
        <div className={tab === 'board' ? undefined : 'hidden'}>
          <BoardTab member={member} onOpenProfile={setProfileId} />
        </div>
        <div className={tab === 'events' ? undefined : 'hidden'}>
          <EventsTab member={member} openCreateOnMount={openCreateEvent} onCreateOpened={() => setOpenCreateEvent(false)} />
        </div>
        <div className={tab === 'food' ? undefined : 'hidden'}>
          <FoodTab />
        </div>
        <div className={tab === 'profile' ? undefined : 'hidden'}>
          <ProfileTab
            member={member}
            onMemberUpdate={setMember}
            onOpenProfile={setProfileId}
            onOpenClubhouse={openClubhouse}
          />
        </div>
      </main>

      <BottomNav
        tab={tab}
        roundsOpen={roundsOpen}
        onTab={goToTab}
        onJustPlay={() => {
          void loadPlayTabData();
          setRoundsOpen(true);
        }}
      />

      {roundsOpen && (
        <Sheet title="Play" onClose={() => setRoundsOpen(false)}>
          <CreateRoundTab ref={roundTabRef} member={member} />
        </Sheet>
      )}

      {composeMode && (
        <ComposeRound
          member={member}
          initialMode={composeMode}
          onClose={() => setComposeMode(null)}
          onPublished={refreshFeed}
          onRoundPublished={handleRoundPublished}
          onCallSettled={(msg) => {
            window.dispatchEvent(new CustomEvent('bagtag:call-settled', { detail: msg }));
          }}
          onAchievement={handleAchievement}
          onAce={(holeNum, courseName) => handleAce(holeNum, courseName)}
        />
      )}
      {openCallShot && <CallYourShotSheet member={member} onClose={() => setOpenCallShot(false)} />}
      {openInvite && <InviteMemberModal member={member} onClose={() => setOpenInvite(false)} />}
      {openMembers && (
        <MembersSheet
          onClose={() => setOpenMembers(false)}
          onOpenProfile={setProfileId}
          onInvite={() => { setOpenMembers(false); setOpenInvite(true); }}
        />
      )}
      {openHandicap && (
        <GhinConnectSheet
          member={member}
          onClose={() => setOpenHandicap(false)}
          onSaved={setMember}
        />
      )}
      {profileId && <ProfileModal memberId={profileId} viewerId={member.id} onClose={() => setProfileId(null)} />}
      {gameId && (
        <GameDetailSheet
          gameId={gameId}
          member={member}
          courseNames={courseNames}
          onClose={() => setGameId(null)}
          onUpdated={() => { refreshGames(); refreshFeed(); }}
        />
      )}
      {wagerState && (
        <WagerModal
          post={wagerState.post}
          member={member}
          memberMap={memberMap}
          isNew={wagerState.isNew}
          onClose={() => setWagerState(null)}
          onSettled={refreshFeed}
        />
      )}
      {gameResult && (
        <GameResultModal
          gameSnapshot={gameResult.post.metadata?.games?.[gameResult.gameIndex]}
          members={memberMap}
          courseName={gameResult.post.metadata?.courseName || gameResult.post.round?.course?.name}
          currentMember={member}
          onClose={() => setGameResult(null)}
        />
      )}
      {settlementRoundId && (
        <RoundResultsSheet
          member={member}
          roundId={settlementRoundId}
          focusSettlement={settlementFocusMoney}
          onClose={() => {
            setSettlementRoundId(null);
            setSettlementFocusMoney(false);
          }}
        />
      )}
      {openActivity && (
        <ActivitySheet
          onClose={() => {
            setOpenActivity(false);
            void db.notifications.unreadCount().then(setUnreadCount);
          }}
        />
      )}
      {openFAQ && <FAQSheet onClose={() => setOpenFAQ(false)} />}
      {aceCelebration && (
        <HoleInOneCelebration
          memberName={member.name}
          holeNum={aceCelebration.holeNum}
          courseName={aceCelebration.courseName}
          onShare={() => void recordAce(true)}
          onSavor={() => void recordAce(false)}
        />
      )}
      {achievementToast && (
        <AchievementCelebrationToast
          achievement={achievementToast}
          onDismiss={() => setAchievementToast(null)}
        />
      )}
      {callSettleToast && (
        <div className="toast-slide-up fixed inset-x-4 bottom-24 z-50 mx-auto max-w-phone">
          <div className="rounded-xl border border-gold/40 bg-navy px-4 py-3 text-sm font-semibold text-cream shadow-lg">
            {callSettleToast}
          </div>
        </div>
      )}
      {summaryPost && (
        <SummaryModal
          post={summaryPost}
          me={member}
          memberMap={memberMap}
          onClose={() => setSummaryPost(null)}
        />
      )}
      {openGamesLib && (
        <GamesLibrarySheet onClose={() => setOpenGamesLib(false)} />
      )}
    </div>
  );
}
